import { useRef } from 'react';
import { ChevronLeft, Check } from 'lucide-react';
import { GooglePostBtn } from './CaptureShareScreen';
import { googleLinkHint } from './googleLinkHint';
import type { InviteResolution } from '../api/captureApi';
import styles from '../pages/ReviewCapturePage.module.css';

// Below-5-star only. Private feedback and the public Google review are shown
// together, as an unforced choice, on one screen — not a sequence where
// Google only unlocks after the private form is submitted. A dissatisfied
// patient who wants to go straight to Google can, from the first screen they
// see; that's what keeps this side of the flow on the right side of the
// FTC's review-gating rule, which targets funnel designs that route negative
// feedback away from the public platform while positive feedback flows to it
// unobstructed. See CaptureShareScreen for the 5-star-only counterpart.
export function CapturePrivateScreen({
  feedback,
  onChangeFeedback,
  patientName,
  onChangePatientName,
  patientContact,
  onChangePatientContact,
  sending,
  done,
  onSubmit,
  onBack,
  resolution,
  shortCode,
  rating,
}: {
  feedback: string;
  onChangeFeedback: (v: string) => void;
  patientName: string;
  onChangePatientName: (v: string) => void;
  patientContact: string;
  onChangePatientContact: (v: string) => void;
  sending: boolean;
  done: boolean;
  onSubmit: () => void;
  onBack: () => void;
  resolution: InviteResolution;
  shortCode: string;
  rating: number | null;
}) {
  const privateAreaRef = useRef<HTMLTextAreaElement>(null);

  return (
    <div className={styles.card}>
      <div className={styles.progress}>
        <div className={`${styles.progressBar} ${styles.progressBarFull}`} />
      </div>

      {done ? (
        <div className={styles.privateDone}>
          <Check size={16} aria-hidden /> Feedback sent — thank you!
        </div>
      ) : (
        <>
          <div className={styles.draftHeader}>
            <div className={styles.question}>Tell the office privately</div>
            {feedback && (
              <button
                type="button"
                className={styles.clearDraftBtn}
                onClick={() => {
                  onChangeFeedback('');
                  privateAreaRef.current?.focus();
                }}
              >
                Write my own
              </button>
            )}
          </div>
          <textarea
            ref={privateAreaRef}
            className={styles.privateArea}
            value={feedback}
            onChange={(e) => onChangeFeedback(e.target.value)}
            placeholder="Type your message here…"
            rows={5}
          />
          {/* Contact info is required for BOTH actions on this screen — sending
              privately or posting to Google — so the office can follow up
              either way. See the disabled Google button below. */}
          <p className={styles.sub}>
            Leave your details so the office can follow up with you — needed whether you send this
            privately or post it to Google.
          </p>
          <div className={styles.contactFields}>
            <input
              type="text"
              className={styles.contactInput}
              value={patientName}
              onChange={(e) => onChangePatientName(e.target.value)}
              placeholder="Your name"
              required
            />
            <input
              type="text"
              className={styles.contactInput}
              value={patientContact}
              onChange={(e) => onChangePatientContact(e.target.value)}
              placeholder="Phone or email"
              required
            />
          </div>
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={!feedback.trim() || !patientName.trim() || !patientContact.trim() || sending}
            onClick={onSubmit}
          >
            {sending ? 'Sending…' : 'Send privately'}
          </button>
        </>
      )}

      {resolution.gbpDeepLink && (
        <>
          <div className={styles.shareDivider}>or</div>
          <GooglePostBtn
            href={resolution.gbpDeepLink}
            draft=""
            shortCode={shortCode}
            rating={rating}
            copyToClipboard={false}
            patientName={patientName}
            patientContact={patientContact}
            disabled={!patientName.trim() || !patientContact.trim()}
            disabledHint="Add your name and phone or email above so the office can follow up, then you can post to Google."
            hint={googleLinkHint(resolution, false)}
            variant="secondary"
          />
        </>
      )}

      <button type="button" className={styles.backBtn} onClick={onBack}>
        <ChevronLeft size={16} aria-hidden /> Back
      </button>
    </div>
  );
}
